package com.icebyte.printprofiletracker

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.js.Date
import kotlin.random.Random

private const val PROFILE_API_URL = "https://ctppt-api.icebyte.network/api/profiles"

private fun toStoredProfile(raw: Any?): StoredProfile {
    val item = raw ?: js("({})")
    return StoredProfile(
        id = (js("item.id") as? String) ?: "profile-${Date().getTime()}",
        title = (js("item.title") as? String) ?: "Untitled",
        description = (js("item.description") as? String) ?: "",
        material = (js("item.material") as? String) ?: "PLA",
        layerHeight = (js("item.layerHeight") as? String) ?: "0.20mm",
        printer = (js("item.printer") as? String) ?: "Prusa MK4",
        notes = (js("item.notes") as? String) ?: "",
        resultCount = (js("item.resultCount") as? Int) ?: 0,
        hasPhoto = (js("item.hasPhoto") as? Boolean) ?: false,
        hasThreeMf = (js("item.hasThreeMf") as? Boolean) ?: false,
    )
}

class JsProfileStore : ProfileStore {
    private suspend fun uploadAttachment(profileId: String, suffix: String, file: Any) {
        val fileType = js("file.type || 'application/octet-stream'") as? String ?: "application/octet-stream"
        val response = window.fetch(
            "https://ctppt-api.icebyte.network/api/attachments/$profileId/$suffix",
            js("({ method: 'POST', headers: { 'Content-Type': fileType }, body: file })")
        ).await()
        if (!response.ok) {
            throw RuntimeException("Failed to upload attachment")
        }
    }

    override suspend fun loadAll(): List<StoredProfile> {
        val response = window.fetch(PROFILE_API_URL, js("({ method: 'GET' })")).await()
        if (!response.ok) return emptyList()
        val payload = response.json().await() as Any?
        val list = js("Array.isArray(payload) ? payload : []") as Array<Any?>
        return list.map { item -> toStoredProfile(item) }
    }

    override suspend fun saveProfile(profile: StoredProfile, photoFiles: List<Any>?, threeMfFile: Any?): String {
        val id = profile.id.ifBlank { "p-${Date().getTime()}-${Random.nextLong(1_000_000_000L, Long.MAX_VALUE)}" }
        val payload = js("({})")
        payload.id = id
        payload.title = profile.title
        payload.description = profile.description
        payload.material = profile.material
        payload.layerHeight = profile.layerHeight
        payload.printer = profile.printer
        payload.notes = profile.notes
        payload.resultCount = profile.resultCount
        payload.hasPhoto = (photoFiles != null && photoFiles.isNotEmpty())
        payload.hasThreeMf = (threeMfFile != null)

        val requestInit = js("({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })")
        val response = window.fetch(PROFILE_API_URL, requestInit).await()

        if (!response.ok) {
            throw RuntimeException("Failed to save profile")
        }

        if (photoFiles != null && photoFiles.isNotEmpty()) {
            uploadAttachment(id, "photo", photoFiles.first())
        }
        if (threeMfFile != null) {
            uploadAttachment(id, "threeMf", threeMfFile)
        }

        val data = response.json().await() as Any?
        return (js("data && data.id ? data.id : null") as? String) ?: id
    }

    override suspend fun getAttachmentUrl(profileId: String, suffix: String): String? {
        if (profileId.isBlank()) return null
        return "https://ctppt-api.icebyte.network/api/attachments/$profileId/$suffix"
    }

    override suspend fun deleteProfile(profileId: String): Boolean {
        if (profileId.isBlank()) return false
        // Use query param for DELETE to avoid body serialization differences between browsers
        val url = "$PROFILE_API_URL?id=$profileId"
        val requestInit = js("({ method: 'DELETE' })")
        val response = window.fetch(url, requestInit).await()
        return response.ok
    }
}

private val jsProfileStoreSingleton: ProfileStore = JsProfileStore()
actual fun getProfileStore(): ProfileStore = jsProfileStoreSingleton
